import React from 'react';
import {
  Box,
  Button,
  ModalBody,
  ModalContent,
  ModalContentProps,
  ModalFooter,
  ModalHeader,
  SimpleGrid,
} from '@chakra-ui/react';
import { ApiErrorCodeMessages, ApiErrorCodeTypes } from 'types/enums';
import { useForm } from 'react-hook-form';
import { InputField, PasswordInput } from 'app/components/Core';
import { Link } from 'types/api.types';
import { dynamicYupResolver } from 'app/utils/utils';
import { AnyObjectSchema } from 'yup';
import { EmailChangeFormLoader } from '../EmailChangeFormLoader';
import { defaultValues } from './utils';

interface EmailChangeForm {
  password: string;
  email: string;
}

export interface EmailChangeDialogFormProps {
  changeEmail: (
    values: EmailChangeForm
  ) => Promise<{ verifyLink: Link | null }>;
  onClose: () => void;
  onSuccess: (newEmail: string) => void;
  isLoading: boolean;
  validationSchema?: AnyObjectSchema;
  modalContentProps?: ModalContentProps;
}

export const EmailChangeDialogForm: React.FC<EmailChangeDialogFormProps> = ({
  changeEmail,
  onClose,
  onSuccess,
  isLoading,
  validationSchema,
  modalContentProps,
}) => {
  const {
    handleSubmit,
    reset,
    setError,
    formState: { isSubmitting, isValid, errors },
    register,
  } = useForm<EmailChangeForm>({
    defaultValues,
    mode: 'all',
    context: {
      yupValidationSchema: validationSchema,
    },
    resolver: dynamicYupResolver,
  });

  const onSubmit = async (values: EmailChangeForm) => {
    try {
      const { verifyLink } = await changeEmail(values);
      if (verifyLink) {
        onSuccess(values.email);
        reset();
      }
    } catch (e: any) {
      if (
        e?.response?.data?.error?.errorCode ===
        ApiErrorCodeTypes.CANNOT_CHANGE_TO_SAME_EMAIL
      ) {
        setError('email', {
          message: ApiErrorCodeMessages.CANNOT_CHANGE_TO_SAME_EMAIL,
        });
      } else {
        setError('password', { message: ApiErrorCodeMessages.BAD_PASSWORD });
      }
    }
  };

  return (
    <ModalContent {...modalContentProps}>
      <ModalHeader title="Change Email Address">
        Change Email Address
      </ModalHeader>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="change-email-form">
        <ModalBody px={4} py={2} overflow="auto" overscrollBehavior="none">
          <SimpleGrid spacingY={4}>
            {isLoading ? (
              <EmailChangeFormLoader />
            ) : (
              <>
                <Box>
                  <InputField
                    label="New Email Address"
                    {...register('email')}
                    isInvalid={Boolean(errors.email)}
                    errorMessage={errors.email?.message}
                    data-testid="settings-email"
                  />
                </Box>
                <Box>
                  <PasswordInput
                    label="Current Password"
                    {...register('password')}
                    isInvalid={Boolean(errors?.password)}
                    errorMessage={errors?.password?.message}
                    data-testid="email-current-password"
                  />
                </Box>
              </>
            )}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} mr={2}>
            Cancel
          </Button>
          <Button
            variant="primary"
            isDisabled={isSubmitting || !isValid}
            type="submit"
          >
            Change
          </Button>
        </ModalFooter>
      </form>
    </ModalContent>
  );
};
